'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useMemo, useState } from 'react';
import { encodeCode128 } from '@/lib/browser-widget-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const MODULE_WIDTHS = [
  { value: '1', label: 'Small' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'Large' },
];

const DEFAULT_BARCODE_TEXT = 'CLEANOR-12345';

export default function BrowserBarcodeGenerator() {
  const [text, setText] = useState(DEFAULT_BARCODE_TEXT);
  const [moduleWidth, setModuleWidth] = useState('2');
  const [showText, setShowText] = useState(true);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(
    () =>
      encodeCode128(text, {
        moduleWidth: Number.parseInt(moduleWidth, 10),
        showText,
      }),
    [text, moduleWidth, showText],
  );

  // A default barcode renders on mount, so `result` alone would count page views rather than
  // use. Only a value the visitor typed themselves counts.
  useToolUsageReport('barcode-generator', text !== DEFAULT_BARCODE_TEXT && text.trim().length > 0);

  async function handleCopySvg() {
    if (!result.ok) return;
    try {
      await copyText(result.svg);
      setCopyMessage('Barcode SVG copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Barcode</span>
        <h2 className="tool-live-title">Barcode generator (Code 128)</h2>
        <p className="tool-live-subtitle">
          Turn any text or number into a scannable Code 128 barcode, rendered as crisp inline SVG.
          Copy the SVG and drop it into labels, packaging, or documents.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Text or number to encode</span>
              <input onChange={(e) => setText(e.target.value)} type="text" value={text} />
            </label>
            <label className="tool-live-field">
              <span>Bar width</span>
              <select onChange={(e) => setModuleWidth(e.target.value)} value={moduleWidth}>
                {MODULE_WIDTHS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}
            >
              <input
                checked={showText}
                onChange={(e) => setShowText(e.target.checked)}
                type="checkbox"
              />
              <span>Show text under bars</span>
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleCopySvg}
              type="button"
            >
              Copy SVG
            </button>
          </div>
          {result.ok === false ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '120px',
              overflowX: 'auto',
            }}
          >
            {result.ok ? (
              <span style={{ maxWidth: '100%' }} dangerouslySetInnerHTML={{ __html: result.svg }} />
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '13px' }}>No barcode to display.</span>
            )}
          </div>
          {result.ok ? (
            <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
              <span>SVG markup</span>
              <textarea readOnly rows={8} value={result.svg} />
            </label>
          ) : null}
          <p className="tool-live-panel-note">
            Encoded locally in your browser with the Code 128 algorithm. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
